import { useMemo } from 'react';

import { ClientShortProfile } from '@ndvr/customer-api';
import { Account, AccountGroup, LifecycleStatus } from '@ndvr/portfolio-api';

import { getClientName } from 'utils';
import { useClientShortProfiles } from 'views/client/client-queries';
import { useHouseholdMap } from 'views/household/household-queries';
import { useListAccountGroupsByLifecycleStatus } from 'views/portfolio/account-group-queries';
import { useListAccountsByLifecycleStatus, useListAccountsForTenant } from 'views/portfolio/account-queries';
import { useWithdrawals } from 'views/portfolio/cash-flow-queries';

import { WithdrawalTableEntry } from './WithdrawalListPage';

/**
 * Utility functions for encoding/decoding withdrawal IDs
 * Format: {cashFlowConfigGUID}_{withdrawalDate}
 */
export function encodeWithdrawalId(cashFlowConfigGUID: string, withdrawalDate: string): string {
    return `${cashFlowConfigGUID}_${withdrawalDate}`;
}

export function decodeWithdrawalId(
    withdrawalId: string,
): { cashFlowConfigGUID: string; withdrawalDate: string } | null {
    const parts = withdrawalId.split('_');
    if (parts.length !== 2) {
        return null;
    }
    return {
        cashFlowConfigGUID: parts[0],
        withdrawalDate: parts[1],
    };
}

/**
 * Options for useWithdrawalData hook
 */
export interface UseWithdrawalDataOptions {
    /**
     * Start date for fetching withdrawals
     */
    startDate: Date;

    /**
     * End date for fetching withdrawals
     */
    endDate: Date;

    /**
     * Whether to filter withdrawals by tenant accounts
     * Used when user doesn't have custody access
     */
    filterByTenant?: boolean;
}

/**
 * Result from useWithdrawalData hook
 */
export interface UseWithdrawalDataResult {
    /** All withdrawals transformed with account, household, and client data */
    withdrawals: WithdrawalTableEntry[] | undefined;

    /** Helper to get a single transformed withdrawal by ID */
    getWithdrawalById: (cashFlowConfigGUID: string, withdrawalDate: string) => WithdrawalTableEntry | undefined;

    /** Combined loading state from all data sources */
    isLoading: boolean;

    /** Whether data is being refetched in the background */
    isUpdating: boolean;

    /** Individual loading states for granular control */
    loadingStates: {
        isLoadingWithdrawals: boolean;
        isLoadingAccounts: boolean;
        isLoadingAccountGroups: boolean;
        isLoadingClients: boolean;
        isLoadingHouseholds: boolean;
        isLoadingTenantAccounts: boolean;
    };

    /** Raw data maps for cases where direct access is needed */
    rawData: {
        accounts: Account[] | undefined;
        accountGroups: AccountGroup[] | undefined;
        clients: ClientShortProfile[] | undefined;
        accountMap: Map<string, Account>;
        accountGroupMap: Map<string, AccountGroup>;
        clientMap: Map<string, ClientShortProfile>;
        tenantAccounts: Account[] | undefined;
    };
}

/**
 * Custom hook for fetching and transforming withdrawal data
 * Combines withdrawals with account, account group, household, and client data
 *
 * @param options - Configuration options for the hook
 * @returns Transformed withdrawal data with helper functions
 *
 */
export function useWithdrawalData(options: UseWithdrawalDataOptions): UseWithdrawalDataResult {
    const { startDate, endDate, filterByTenant = false } = options;

    // Fetch all required data
    const {
        data: rawWithdrawals,
        isPending: isLoadingWithdrawals,
        isFetching: isFetchingWithdrawals,
    } = useWithdrawals(startDate, endDate);

    const { data: accounts, isPending: isLoadingAccounts } = useListAccountsByLifecycleStatus([
        LifecycleStatus.CLOSED,
        LifecycleStatus.REAL,
    ]);

    const { data: accountGroups, isPending: isLoadingAccountGroups } = useListAccountGroupsByLifecycleStatus([
        LifecycleStatus.CLOSED,
        LifecycleStatus.REAL,
    ]);

    const { data: clients, isPending: isLoadingClients } = useClientShortProfiles();
    const households = useHouseholdMap();
    const isLoadingHouseholds = !households;

    const { data: tenantAccounts, isPending: isLoadingTenantAccounts } = useListAccountsForTenant();

    // Create account map
    const accountMap = useMemo(
        () =>
            (accounts || []).reduce(
                (map, account) => map.set(account.accountGUID, account),
                new Map<string, Account>(),
            ),
        [accounts],
    );

    // Create account group map
    const accountGroupMap = useMemo(
        () =>
            (accountGroups || []).reduce(
                (map, accountGroup) => map.set(accountGroup.accountGroupGUID, accountGroup),
                new Map<string, AccountGroup>(),
            ),
        [accountGroups],
    );

    // Create client map
    const clientMap = useMemo(
        () =>
            (clients || []).reduce(
                (map, client) => map.set(client.clientGUID, client),
                new Map<string, ClientShortProfile>(),
            ),
        [clients],
    );

    // Filter withdrawals by tenant if requested
    const filteredWithdrawals = useMemo(() => {
        if (!filterByTenant || !tenantAccounts) {
            return rawWithdrawals;
        }

        const allowedAccountGroupGuids = new Set((tenantAccounts || []).map((a: Account) => a.accountGroupGUID));
        return (rawWithdrawals || []).filter((w) =>
            w.accountGroupGUID ? allowedAccountGroupGuids.has(w.accountGroupGUID) : false,
        );
    }, [rawWithdrawals, tenantAccounts, filterByTenant]);

    // Transform withdrawals with account, account group, household, and client data
    const withdrawals = useMemo(() => {
        if (!filteredWithdrawals) return undefined;

        return filteredWithdrawals.map((withdrawal): WithdrawalTableEntry => {
            const account = accountMap.get(withdrawal.accountGUID || '');
            const accountGroup = accountGroupMap.get(withdrawal.accountGroupGUID);
            const household = households?.get(accountGroup?.householdGUID || '');
            const client = clientMap.get(account?.clientGUID || '');

            return {
                ...withdrawal,
                cashFlowConfigGUID: withdrawal.cashFlowConfigGUID,
                portfolioName: accountGroup?.accountGroupName?.plainText,
                accountGUID: withdrawal.accountGUID,
                accountGroupGUID: withdrawal.accountGroupGUID,
                accountName: account?.accountName?.plainText,
                custodyId: account?.custodyAccountId,
                custodyType: account?.custodyType,
                householdGUID: accountGroup?.householdGUID || '',
                householdName: household?.globalAccountNamePlain,
                clientGUID: client?.clientGUID,
                ownerPersonOrgGUID: client?.personOrgGUID,
                clientName: getClientName(client),
            };
        });
    }, [filteredWithdrawals, accountMap, accountGroupMap, households, clientMap]);

    // Helper function to get a single withdrawal by ID
    const getWithdrawalById = useMemo(
        () =>
            (cashFlowConfigGUID: string, withdrawalDate: string): WithdrawalTableEntry | undefined => {
                return withdrawals?.find(
                    (w) => w.cashFlowConfigGUID === cashFlowConfigGUID && w.withdrawalDate === withdrawalDate,
                );
            },
        [withdrawals],
    );

    // Combined loading state
    const isLoading =
        isLoadingWithdrawals ||
        isLoadingAccounts ||
        isLoadingAccountGroups ||
        isLoadingClients ||
        isLoadingHouseholds ||
        (filterByTenant && isLoadingTenantAccounts);

    return {
        withdrawals,
        getWithdrawalById,
        isLoading,
        isUpdating: isFetchingWithdrawals,
        loadingStates: {
            isLoadingWithdrawals,
            isLoadingAccounts,
            isLoadingAccountGroups,
            isLoadingClients,
            isLoadingHouseholds,
            isLoadingTenantAccounts,
        },
        rawData: {
            accounts,
            accountGroups,
            clients,
            accountMap,
            accountGroupMap,
            clientMap,
            tenantAccounts,
        },
    };
}
